import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CustomerAuthContext = createContext();

export const CustomerAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('sv-customer-token'));
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeader = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token) {
        setCustomer(null);
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/customer/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setCustomer(res.data);
      } catch {
        if (active) {
          localStorage.removeItem('sv-customer-token');
          setToken(null);
          setCustomer(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [token]);

  const saveToken = (t) => {
    localStorage.setItem('sv-customer-token', t);
    setToken(t);
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/customer/register`, { name, email, password });
    setCustomer(res.data.customer);
    saveToken(res.data.token);
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/customer/login`, { email, password });
    setCustomer(res.data.customer);
    saveToken(res.data.token);
  };

  const logout = () => {
    localStorage.removeItem('sv-customer-token');
    setToken(null);
    setCustomer(null);
  };

  return (
    <CustomerAuthContext.Provider value={{ token, customer, loading, register, login, logout, authHeader }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
};
